import { Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useAppToast } from '../../../helpers'
import { IAbraMidiaListItem, IAbraMidiaRaw } from '../../../interfaces/AbraMidia'
import AbraMidiaService from '../../../services/AbraMidia/AbraMidiaService'
import { AbraMidiaInfoModal } from './AbraMidiaInfoModal'

export function AbraMidiaPage() {
  const abraMidiaService = new AbraMidiaService()
  const toast = useAppToast()
  const [data, setData] = useState<IAbraMidiaListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState(3)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [infoModalTextHeader, setInfoModalTextHeader] = useState('')
  const [infoModalCode, setInfoModalCode] = useState('')

  const handleOpenModal = (title: string, code: string) => {
    setInfoModalTextHeader(title)
    setInfoModalCode(code)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const loadAbraMidias = (page: number) => {
    setLoading(true)
    setLoadError(false)
    abraMidiaService
      .list({ page, pageSize })
      .then((response) => {
        setData(
          response.data.data.map((abraMidia: IAbraMidiaRaw) => ({
            id: abraMidia.id,
            title: abraMidia.title,
            code: abraMidia.code,
            createdAt: formatDateTime(abraMidia.created_at),
            updatedAt: formatDateTime(abraMidia.updated_at)
          }))
        )
        setPage(response.data.current_page)
        setTotalPages(Math.ceil(response.data.total / response.data.per_page))
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os materiais', error)
      })
      .finally(() => setLoading(false))
  }

  const handleDelete = async () => {
    if (!deletingId) return

    setShowConfirmModal(false)
    abraMidiaService.delete(deletingId).then(() => {
      toast.success('ABRA Mídia excluído com sucesso!')
    })
    loadAbraMidias(1)
  }

  useEffect(() => {
    loadAbraMidias(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize])

  return (
    <>
      <PageTitle title="ABRA Mídia" description="Materiais de mídia disponibilizados por código" />

      <ActionFab to="/abra-midia/new" label="Adicionar mídia" openInNewTab />

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Atualizado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={5} />
            ) : loadError ? (
              <TableErrorRow colSpan={5} entity="materiais" onRetry={() => loadAbraMidias(page)} />
            ) : data.length === 0 ? (
              <TableEmptyRow colSpan={5} entity="material" />
            ) : (
              data.map((item) => {
                return (
                  <Tr key={item.id}>
                    <Td>{item.id}</Td>
                    <Td>
                      <ShortText text={item.title} beforeChars={40} afterChars={0} />
                    </Td>
                    <Td>{item.createdAt}</Td>
                    <Td>
                      {item.updatedAt === item.createdAt ? (
                        <Text align={'center'} color="gray.500">
                          -
                        </Text>
                      ) : (
                        item.updatedAt
                      )}
                    </Td>
                    <Td>
                      <ActionButton
                        action="link"
                        colorScheme="green"
                        variant="solid"
                        onClick={() => {
                          handleOpenModal(item.title, item.code)
                        }}
                      />
                      <ActionButton action="edit" to={`/abra-midia/${item.id}`} />
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setShowConfirmModal(true)
                          setDeletingId(item.id)
                        }}
                      />
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadAbraMidias(page)
        }}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <AbraMidiaInfoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        textHeader={infoModalTextHeader}
        code={infoModalCode}
      />

      <ConfirmDialog
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleDelete}
        action="delete"
        entity="publicação"
        feminine
      />
    </>
  )
}
